import { MRT_ColumnDef } from "mantine-react-table"
import { ResourceObjectDto } from "../../../../@client/Theasoft_ApiServer_ResourceObjects"
import { ResourceImage } from "./ResourceImage"
import {
  FieldData,
  FieldFilter,
  getUpdatedFilterDict,
} from "../../../../-components/field-data"
import { ResourceTypeTreeSelect } from "./ResourceTypeTreeSelect"
import { ConditionSelect } from "./ConditionSelect"
import { ExtendedInfiniteScrollingTableNewApi } from "../../-components/infinite-scrolling-table-new-api/extended-infinite-scrolling-table"
import { Checkbox, TextInput } from "@mantine/core"
import { useNavigate, useSearch } from "@tanstack/react-router"
import useGetResourceObjectFieldTypes from "./useGetResourceObjectFieldTypes"
import useGetResourceFieldTypes from "./useGetResourceFieldTypes"
import { IconCheckBox } from "../../-components/icon-check-box"
import { ResourceTypeCategory } from "../../../../@client/Theasoft_DataClasses_Dispo"
import { useTranslation } from "react-i18next"
import { getFileType } from "@/utils/getFileType"
import { ResourceObjectFilter } from "@/orval/gen/model"
import { getPaginatedResourceObjects } from "@/orval/gen"

export function ResourceObjectsTable({
  category,
}: {
  category: ResourceTypeCategory
}) {
  const { data: fieldTypes } = useGetResourceFieldTypes(category)
  const { data: objectFieldTypes } = useGetResourceObjectFieldTypes(category)
  const search = useSearch({ from: "/app/resources/resource-objects" })
  const { filter } = search
  const navigate = useNavigate()
  const updateFilter = (newValue: ResourceObjectFilter) =>
    navigate({
      to: "/app/resources/resource-objects",
      search: { filter: { ...filter, ...newValue } },
      replace: true,
    })
  const updateSearch = (newValue: Partial<typeof search>) => {
    navigate({
      to: "/app/resources/resource-objects",
      search: { ...search, ...newValue },
      replace: true,
    })
  }

  React.useEffect(() => {
    updateFilter({ resourceTypeTreeNodes: undefined })
  }, [category])

  const { t } = useTranslation("app_server")

  const columns = React.useMemo<MRT_ColumnDef<ResourceObjectDto>[]>(
    () => [
      {
        accessorKey: "name",
        header: t("Name"),
        Filter: () => (
          <TextInput
            value={filter?.name ?? undefined}
            onChange={(e) => updateFilter({ name: e.target.value })}
          />
        ),
      },
      {
        accessorKey: "identificationCode",
        header: t("Code"),
        Filter: () => (
          <TextInput
            value={filter?.identification ?? undefined}
            onChange={(e) => updateFilter({ identification: e.target.value })}
          />
        ),
      },
      {
        accessorKey: "type",
        header: t("Type"),
        Filter: () => (
          <ResourceTypeTreeSelect
            category={category}
            flex={1}
            initialCheckedState={filter?.resourceTypeTreeNodes ?? []}
            onCheckedStateChange={(values) =>
              updateFilter({ resourceTypeTreeNodes: values })
            }
          />
        ),
      },
      {
        accessorKey: "condition",
        header: t("Condition"),
        Filter: () => (
          <ConditionSelect
            clearable
            value={filter?.condition?.toString() ?? undefined}
            onChange={(value) =>
              updateFilter({ condition: value ? parseInt(value) : undefined })
            }
          />
        ),
      },
      {
        accessorKey: "available",
        header: t("Available"),
        Cell: ({ row }) => <IconCheckBox checked={row.original.available} />,
        enableSorting: false,
        Filter: () => (
          <Checkbox
            checked={filter?.available}
            onChange={() => updateFilter({ available: !filter?.available })}
          />
        ),
      },
      {
        accessorKey: "attachmentId",
        header: t("Picture"),
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const image = row.original.attachments.find(
            (a) => getFileType(a.fileName) === "image"
          )
          if (!image) return null
          return (
            <ResourceImage
              attachmentId={image.attachmentId}
              resourceId={row.original.resourceId}
              h={"100%"}
              w={"100%"}
              mah={100}
            />
          )
        },
      },
      ...fieldTypes.map<MRT_ColumnDef<ResourceObjectDto>>((type) => ({
        header: type.name,
        accessorKey: type.name,
        enableSorting: false,
        Cell: (cell) => (
          <FieldData
            fieldData={cell.row.original.resourceFields.find(
              (field) => field.name == type.name
            )}
          />
        ),
        Filter: () => (
          <FieldFilter
            fieldType={type}
            value={filter?.fieldFilter?.[type.id]?.values}
            onChange={(value) =>
              updateFilter({
                fieldFilter: getUpdatedFilterDict(
                  type.id,
                  { dataType: type.dataType, values: value },
                  filter?.fieldFilter
                ),
              })
            }
          />
        ),
      })),
      ...objectFieldTypes.map<MRT_ColumnDef<ResourceObjectDto>>((type) => ({
        header: type.name,
        accessorKey: type.name,
        enableSorting: false,
        Cell: (cell) => (
          <FieldData
            fieldData={cell.row.original.objectFields.find(
              (field) => field.name == type.name
            )}
          />
        ),
        Filter: () => {
          return (
            <FieldFilter
              fieldType={type}
              value={filter?.fieldFilter?.[type.id]?.values}
              onChange={(value) =>
                updateFilter({
                  fieldFilter: getUpdatedFilterDict(
                    type.id,
                    { dataType: type.dataType, values: value },
                    filter?.fieldFilter
                  ),
                })
              }
            />
          )
        },
      })),
    ],
    [filter]
  )
  return (
    <ExtendedInfiniteScrollingTableNewApi
      queryFn={getPaginatedResourceObjects}
      columnDefinitions={columns}
      defaultSorting={[{ id: "resourceObjectId", desc: false }]}
      getParams={(pageRequest) => ({
        ...pageRequest,
        ...search,
        filter: { ...filter, category },
      })}
      sorting={search.sorting ?? []}
      onSortingChange={(value) => updateSearch({ sorting: value })}
      globalFilter={search.globalFilter ?? ""}
      onGlobalFilterChange={(value) => updateSearch({ globalFilter: value })}
    />
  )
}
